import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Row,
    Col,
    Table,
    Badge,
    Button,
    Spinner,
    ProgressBar,
    Alert,
} from 'react-bootstrap';
import axios from 'axios';
import { getUserFromLocalStorage } from '../../utilities/user';
import socket from '../../utilities/socket';
import PartnerDeliveryEarnings from '../../components/money/PartnerDeliveryEarnings';
import { colors, font, text, cardBody, pill } from './partnerTheme';
import PartnerHelp from './partnerHelp';

const API_URL = process.env.REACT_APP_SERVER_URL || '';
const WALLET_URL = process.env.REACT_APP_WALLET_SERVICE_URL || 'http://localhost:8005';

/**
 * A quiet card header. The saturated navy bar is gone; identity is carried by
 * one small dot in the same hue over the card's own surface, so nothing on the
 * page competes with the figures underneath it.
 */
const QuietHeader = ({ title, accent, icon, right }) => (
    <CardHeader
        style={{
            background: colors.surface,
            border: 'none',
            borderBottom: `1px solid ${colors.line}`,
            padding: '18px 28px 14px',
        }}
    >
        <div className="d-flex justify-content-between align-items-center">
            <CardTitle
                tag="h5"
                className="mb-0"
                style={{ ...text.title, display: 'inline-flex', alignItems: 'center', gap: 9 }}
            >
                <span style={{ width: 8, height: 8, borderRadius: 999, background: accent, flexShrink: 0 }} />
                {icon ? <i className={`nc-icon ${icon}`} style={{ color: colors.faint, fontSize: 15 }} /> : null}
                {title}
            </CardTitle>
            {right}
        </div>
    </CardHeader>
);

/**
 * One line of the split: who it goes to and what share, the amount aligned on
 * the same baseline, and a thin rounded bar reading the percentage.
 */
const ShareLine = ({ label, share, amount, variant, valueColor }) => (
    <div style={{ marginBottom: 18 }}>
        <div className="d-flex justify-content-between align-items-baseline" style={{ marginBottom: 7 }}>
            <span style={{ fontFamily: font.sans, fontSize: 13, color: colors.muted }}>
                {label}
                <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', marginLeft: 6, color: colors.faint }}>
                    {share}%
                </span>
            </span>
            <span
                style={{
                    fontFamily: font.mono,
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 15,
                    fontWeight: 600,
                    color: valueColor || colors.ink,
                    whiteSpace: 'nowrap',
                }}
            >
                {amount}
            </span>
        </div>
        <ProgressBar
            now={share}
            variant={variant}
            style={{ height: 5, borderRadius: 999, background: colors.lineSoft }}
        />
    </div>
);

/**
 * The delivery fee the split is illustrated against.
 *
 * This card used to be handed `averagePerDelivery * 5`, which is five
 * deliveries' revenue rendered under the words "example delivery fee", so a
 * partner averaging UGX 4,200 was told their example fee was UGX 21,000 and
 * every share below it was computed off that. A worked example has to be a
 * round, recognisable number, not an arithmetic accident.
 *
 * Ten thousand rather than the partner's own baseDeliveryFee: that field is the
 * floor for a short hop, so illustrating the split at it understates every
 * line. This is a typical fee, and the copy says it is an example rather than
 * implying it is theirs.
 */
const ILLUSTRATIVE_DELIVERY_FEE = 10000;

const PartnerEarnings = () => {
    const { agent } = useSelector((state) => state.auth);
    const user = agent || getUserFromLocalStorage();
    const partnerId = user?.result?.partnerId || user?.result?._id;
    const token = user?.token;

    const [loading, setLoading] = useState(true);
    const [earningsData, setEarningsData] = useState(null);
    const [riderEarnings, setRiderEarnings] = useState([]);
    const [error, setError] = useState('');
    // Batch payout history (from wallet service)
    const [batches, setBatches] = useState([]);
    const [batchSummary, setBatchSummary] = useState(null);
    const [batchPage, setBatchPage] = useState(1);
    const [batchPages, setBatchPages] = useState(1);
    const [batchTotal, setBatchTotal] = useState(0);
    const [batchLoading, setBatchLoading] = useState(false);
    const [batchStatusFilter, setBatchStatusFilter] = useState('');
    const [expandedBatch, setExpandedBatch] = useState(null);
    const [period, setPeriod] = useState('thisMonth');

    // Commission settings state - will be fetched from partner settings
    const [commissionSettings, setCommissionSettings] = useState({
        commissionRate: 40,      // Lusuku's percentage (default)
        partnerShareRate: 18,    // Partner's percentage (default)
        riderShareRate: 42,      // Rider's percentage (default)
        baseDeliveryFee: 5000
    });

    // Track last update for visual feedback
    const [lastUpdate, setLastUpdate] = useState(null);

    // Handler for completed orders - updates earnings in real-time
    const handleOrderCompleted = ({ orderId, riderId, earnings }) => {
        // Calculate partner's share based on rider earnings
        // If rider gets 42% (e.g., UGX 2,100), partner gets 18% (e.g., UGX 900)
        const partnerEarningsFromOrder = Math.round(
            earnings * (commissionSettings.partnerShareRate / commissionSettings.riderShareRate)
        );

        setEarningsData(prev => {
            if (!prev) return prev;
            const newDeliveries = (prev.totalDeliveries || 0) + 1;
            const newPartnerEarnings = (prev.partnerEarnings || 0) + partnerEarningsFromOrder;
            return {
                ...prev,
                partnerEarnings: newPartnerEarnings,
                riderPayouts: (prev.riderPayouts || 0) + earnings,
                totalDeliveries: newDeliveries
            };
        });

        // Update rider breakdown if rider is in the list
        setRiderEarnings(prev => {
            const riderIndex = prev.findIndex(r => r._id === riderId || r.riderId === riderId);
            if (riderIndex >= 0) {
                const updated = [...prev];
                updated[riderIndex] = {
                    ...updated[riderIndex],
                    deliveries: (updated[riderIndex].deliveries || 0) + 1,
                    totalEarnings: (updated[riderIndex].totalEarnings || 0) + earnings,
                    netEarnings: (updated[riderIndex].netEarnings || 0) + earnings
                };
                return updated;
            }
            return prev;
        });

        setLastUpdate(new Date());
    };

    useEffect(() => {
        fetchBatchHistory(batchPage, batchStatusFilter);
    }, [batchPage, batchStatusFilter, partnerId, token]);

    useEffect(() => {
        if (partnerId && token) {
            fetchEarningsData();
            fetchBatchHistory(1, '');
            fetchCommissionSettings();

            // Join partner room for real-time updates
            socket.emit('join3PLPartnerRoom', partnerId);

            // Request commission settings via socket
            socket.emit('partner:getCommissionSettings', partnerId);

            // Listen for commission updates
            socket.on('partner:commissionUpdated', handleCommissionUpdate);
            socket.on('partner:commissionSettings', handleCommissionUpdate);

            // Listen for order completions - real-time earnings updates
            socket.on('order:completed', handleOrderCompleted);

            // Listen for stats updates - trigger refresh
            socket.on('partner:statsUpdate', () => {
                fetchEarningsData();
                setLastUpdate(new Date());
            });

            return () => {
                socket.emit('leave3PLPartnerRoom', partnerId);
                socket.off('partner:commissionUpdated', handleCommissionUpdate);
                socket.off('partner:commissionSettings', handleCommissionUpdate);
                socket.off('order:completed', handleOrderCompleted);
                socket.off('partner:statsUpdate');
            };
        } else {
            setLoading(false);
            setError('Partner ID not found');
        }
    }, [partnerId, token, period]);

    const handleCommissionUpdate = (data) => {
        if (data.partnerId === partnerId || !data.partnerId) {
            setCommissionSettings({
                commissionRate: data.commissionRate ?? 40,
                partnerShareRate: data.partnerShareRate ?? 18,
                riderShareRate: data.riderShareRate ?? 42,
                baseDeliveryFee: data.baseDeliveryFee ?? 5000
            });
        }
    };

    const fetchCommissionSettings = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/3pl-partners/${partnerId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                const partner = response.data.data?.partner;
                if (partner) {
                    setCommissionSettings({
                        commissionRate: partner.commissionRate ?? 40,
                        partnerShareRate: partner.partnerShareRate ?? 18,
                        riderShareRate: partner.riderShareRate ?? 42,
                        baseDeliveryFee: partner.baseDeliveryFee ?? 5000
                    });
                }
            }
        } catch (err) {
            console.error('Error fetching commission settings:', err);
            // Keep defaults on error
        }
    };

    const fetchEarningsData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/api/3pl-partners/${partnerId}/earnings`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { period },
            });
            if (response.data.success) {
                setEarningsData(response.data.data);
                // Backend returns 'breakdown', not 'riderBreakdown'
                setRiderEarnings(response.data.data?.breakdown || []);
            }
        } catch (err) {
            console.error('Error fetching earnings:', err);
            // Set default data
            setEarningsData({
                totalEarnings: 0,
                partnerShare: 0,
                riderPayouts: 0,
                lusakuCommission: 0,
                deliveriesCompleted: 0,
                averagePerDelivery: 0,
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchBatchHistory = async (page = 1, statusFilter = '') => {
        if (!partnerId || !token) return;
        setBatchLoading(true);
        try {
            let url = `${WALLET_URL}/api/disbursement/partners/${partnerId}/batch-history?page=${page}&limit=15`;
            if (statusFilter) url += `&status=${statusFilter}`;
            const response = await axios.get(url, {
                headers: { 'x-auth-token': token },
            });
            setBatches(response.data.batches || []);
            setBatchSummary(response.data.summary || null);
            setBatchPages(response.data.pagination?.pages || 1);
            setBatchTotal(response.data.pagination?.total || 0);
            setBatchPage(response.data.pagination?.page || 1);
        } catch (err) {
            console.error('[PartnerEarnings] fetchBatchHistory error:', err);
            setBatches([]);
        } finally {
            setBatchLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return `UGX ${(amount || 0).toLocaleString()}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-UG', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    // Commission breakdown visualization - now uses dynamic values
    const CommissionBreakdown = ({ total }) => {
        const lusakuShare = total * (commissionSettings.commissionRate / 100);
        const partnerShare = total * (commissionSettings.partnerShareRate / 100);
        const riderShare = total * (commissionSettings.riderShareRate / 100);

        return (
            <Card>
                <QuietHeader
                    title={
                        <>
                            Commission Breakdown
                            <PartnerHelp topic="commissionSplit" />
                        </>
                    }
                    accent={colors.blue}
                />
                <CardBody style={cardBody}>
                    <p style={{ fontFamily: font.sans, fontSize: 13, color: colors.muted, marginBottom: 22 }}>
                        How a {formatCurrency(total)} delivery fee is split at your current rates.
                        The percentages are yours; the fee is an example.
                    </p>

                    <ShareLine
                        label="Lusuku Platform"
                        share={commissionSettings.commissionRate}
                        amount={formatCurrency(lusakuShare)}
                        variant="danger"
                    />
                    <ShareLine
                        label="Partner Share"
                        share={commissionSettings.partnerShareRate}
                        amount={formatCurrency(partnerShare)}
                        variant="success"
                        valueColor={colors.green}
                    />
                    <ShareLine
                        label="Rider Earnings"
                        share={commissionSettings.riderShareRate}
                        amount={formatCurrency(riderShare)}
                        variant="info"
                    />

                    <div
                        className="d-flex justify-content-between align-items-baseline"
                        style={{ borderTop: `1px solid ${colors.line}`, paddingTop: 14, marginTop: 4 }}
                    >
                        <span style={{ fontFamily: font.sans, fontSize: 13, fontWeight: 600, color: colors.body }}>
                            Total Delivery Fee
                        </span>
                        <span
                            style={{
                                fontFamily: font.mono,
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: 19,
                                fontWeight: 600,
                                color: colors.ink,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {formatCurrency(total)}
                        </span>
                    </div>
                </CardBody>
            </Card>
        );
    };

    const BATCH_STATUS_META = {
        COMPLETED:            { label: 'Completed',        variant: 'success',   color: '#16a34a' },
        PROCESSING:           { label: 'Processing',       variant: 'info',      color: '#2563eb' },
        APPROVED:             { label: 'Approved',         variant: 'primary',   color: '#7c3aed' },
        AWAITING_APPROVAL:    { label: 'Awaiting Approval',variant: 'warning',   color: '#d97706' },
        PARTIALLY_COMPLETED:  { label: 'Partial',          variant: 'warning',   color: '#f59e0b' },
        FAILED:               { label: 'Failed',           variant: 'danger',    color: '#dc2626' },
        REJECTED:             { label: 'Rejected',         variant: 'danger',    color: '#dc2626' },
        PREPARING:            { label: 'Preparing',        variant: 'secondary', color: '#6b7280' },
        CANCELLED:            { label: 'Cancelled',        variant: 'secondary', color: '#6b7280' },
    };

    if (loading) {
        return (
            <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="grow" style={{ color: "rgba(25, 82, 110, 0.84)" }} />
            </div>
        );
    }

    return (
        <div className="content">
            <Row className="mb-4">
                <Col>
                    <h4 style={{ color: colors.body }}>
                        <i className="nc-icon nc-money-coins me-2" />
                        Earnings & Payouts
                        <PartnerHelp topic="earningsVsPayouts" />
                    </h4>
                    <div className="d-flex align-items-center gap-2">
                        <p style={{ color: colors.muted, marginBottom: 0 }}>Track your revenue and payout history</p>
                        <Badge
                            bg=""
                            style={{
                                fontSize: '10px',
                                padding: '4px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                backgroundColor: '#03ab06d1'
                            }}
                        >
                            <span style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: '#fff',
                                animation: 'pulse 1s infinite'
                            }} />
                            Live Updates
                        </Badge>
                        {lastUpdate && (
                            <small style={{ color: colors.muted, fontSize: '11px' }}>
                                Updated {new Date(lastUpdate).toLocaleTimeString()}
                            </small>
                        )}
                    </div>
                </Col>
                <Col className="text-end">
                    <Button
                        variant={period === 'today' ? 'primary' : 'outline-primary'}
                        size="sm"
                        className="me-1"
                        onClick={() => setPeriod('today')}
                    >
                        Today
                    </Button>
                    <Button
                        variant={period === 'thisWeek' ? 'primary' : 'outline-primary'}
                        size="sm"
                        className="me-1"
                        onClick={() => setPeriod('thisWeek')}
                    >
                        This Week
                    </Button>
                    <Button
                        variant={period === 'thisMonth' ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={() => setPeriod('thisMonth')}
                    >
                        This Month
                    </Button>
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* Server-computed delivery economics for this partner. The cards
                below are the live operational view; this is the reconciled one,
                built from the same order economics Lusuku reports on. */}
            <Row className="mb-4">
                <Col xs="12">
                    <PartnerDeliveryEarnings partnerId={partnerId} title="Delivery earnings, reconciled" />
                </Col>
            </Row>

            {/* Earnings Summary Cards */}
            <Row className="mb-4">
                <Col lg="3" md="6" sm="6">
                    <Card className="card-stats">
                        <CardBody>
                            <Row>
                                <Col xs="5">
                                    <div className="icon-big text-center" style={{ color: colors.green }}>
                                        <i className="nc-icon nc-money-coins" />
                                    </div>
                                </Col>
                                <Col xs="7">
                                    <div className="numbers">
                                        <p className="card-category">Your Earnings</p>
                                        <CardTitle tag="h4" style={{ color: colors.green }}>
                                            {formatCurrency(earningsData?.partnerEarnings)}
                                        </CardTitle>
                                    </div>
                                </Col>
                            </Row>
                            <hr />
                            <div className="stats" style={{ color: colors.muted }}>
                                <i className="nc-icon nc-chart-bar-32 me-1" />
                                {commissionSettings.partnerShareRate}% of total delivery fees
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="3" md="6" sm="6">
                    <Card className="card-stats">
                        <CardBody>
                            <Row>
                                <Col xs="5">
                                    <div className="icon-big text-center" style={{ color: colors.blue }}>
                                        <i className="nc-icon nc-single-02" />
                                    </div>
                                </Col>
                                <Col xs="7">
                                    <div className="numbers">
                                        <p className="card-category">Rider Payouts</p>
                                        <CardTitle tag="h4">
                                            {formatCurrency(earningsData?.riderPayouts)}
                                        </CardTitle>
                                    </div>
                                </Col>
                            </Row>
                            <hr />
                            <div className="stats" style={{ color: colors.muted }}>
                                <i className="nc-icon nc-delivery-fast me-1" />
                                {commissionSettings.riderShareRate}% to your riders
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="3" md="6" sm="6">
                    <Card className="card-stats">
                        <CardBody>
                            <Row>
                                <Col xs="5">
                                    <div className="icon-big text-center" style={{ color: colors.teal }}>
                                        <i className="nc-icon nc-check-2" />
                                    </div>
                                </Col>
                                <Col xs="7">
                                    <div className="numbers">
                                        <p className="card-category">Deliveries</p>
                                        <CardTitle tag="h4">
                                            {earningsData?.totalDeliveries || 0}
                                        </CardTitle>
                                    </div>
                                </Col>
                            </Row>
                            <hr />
                            <div className="stats" style={{ color: colors.muted }}>
                                <i className="nc-icon nc-time-alarm me-1" />
                                Completed deliveries
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="3" md="6" sm="6">
                    <Card className="card-stats">
                        <CardBody>
                            <Row>
                                <Col xs="5">
                                    <div className="icon-big text-center" style={{ color: colors.amber }}>
                                        <i className="nc-icon nc-chart-pie-35" />
                                    </div>
                                </Col>
                                <Col xs="7">
                                    <div className="numbers">
                                        <p className="card-category">Avg/Delivery</p>
                                        <CardTitle tag="h4">
                                            {formatCurrency(earningsData?.totalDeliveries > 0 ? earningsData?.partnerEarnings / earningsData?.totalDeliveries : 0)}
                                        </CardTitle>
                                    </div>
                                </Col>
                            </Row>
                            <hr />
                            <div className="stats" style={{ color: colors.muted }}>
                                <i className="nc-icon nc-bullet-list-67 me-1" />
                                Your avg. per order
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                {/* Commission Breakdown */}
                <Col lg="5">
                    <CommissionBreakdown total={ILLUSTRATIVE_DELIVERY_FEE} />
                </Col>

                {/* Rider Earnings Breakdown */}
                <Col lg="7">
                    <Card>
                        <QuietHeader title="Earnings by Rider" accent={colors.green} />
                        <CardBody style={cardBody}>
                            {riderEarnings.length === 0 ? (
                                <p
                                    className="text-center"
                                    style={{ fontFamily: font.sans, fontSize: 13, color: colors.muted, margin: '28px 0' }}
                                >
                                    No rider earnings recorded for this period yet.
                                </p>
                            ) : (
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Rider</th>
                                            <th>Deliveries</th>
                                            <th>Total Earnings</th>
                                            <th>Net Earned</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {riderEarnings.map((rider) => (
                                            <tr key={rider._id || rider.riderId}>
                                                <td>
                                                    <strong>{rider.riderName || 'Unknown Rider'}</strong>
                                                </td>
                                                <td>{rider.deliveries || 0}</td>
                                                <td>{formatCurrency(rider.totalEarnings)}</td>
                                                <td style={{ color: colors.green }}>
                                                    <strong>{formatCurrency(rider.netEarnings || rider.totalEarnings)}</strong>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Batch Payout History */}
            <Row className="mt-4">
                <Col>
                    <Card>
                        <QuietHeader
                            title="Batch Payout History"
                            accent={colors.blue}
                            icon="nc-credit-card"
                            right={
                                <span
                                    style={{
                                        ...pill('muted'),
                                        fontFamily: font.mono,
                                        fontSize: 11,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {batchTotal} Batches
                                </span>
                            }
                        />
                        <CardBody>
                            {/* Summary cards */}
                            {batchSummary && (
                                <Row className="mb-3">
                                    {[
                                        { label: 'Total Received', value: formatCurrency(batchSummary.totalDisbursed), color: colors.green, icon: 'nc-money-coins' },
                                        { label: 'In Progress',    value: formatCurrency(batchSummary.totalPending),   color: colors.amber, icon: 'nc-time-alarm' },
                                        { label: 'Batches',        value: batchSummary.totalBatches,                    color: colors.blue,   icon: 'nc-chart-bar-32' },
                                        { label: 'Riders Paid',    value: batchSummary.totalRidersPaid,                 color: colors.teal, icon: 'nc-single-02' },
                                    ].map((c) => (
                                        <Col key={c.label} xs="6" md="3" className="mb-2">
                                            <div style={{ background: colors.ground, borderRadius: 10, padding: '10px 14px', borderLeft: `4px solid ${c.color}` }}>
                                                <div style={{ fontSize: 11, color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{c.label}</div>
                                                <div style={{ fontSize: 15, fontWeight: 700, color: c.color, marginTop: 2 }}>{c.value}</div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            )}

                            {/* Status filter chips */}
                            <div className="d-flex flex-wrap gap-1 mb-3">
                                {[['', 'All'], ['COMPLETED', 'Completed'], ['AWAITING_APPROVAL', 'Awaiting Approval'], ['PROCESSING', 'Processing'], ['FAILED', 'Failed']].map(([val, label]) => (
                                    <Button
                                        key={val}
                                        size="sm"
                                        variant={batchStatusFilter === val ? 'primary' : 'outline-secondary'}
                                        onClick={() => { setBatchStatusFilter(val); setBatchPage(1); }}
                                        style={{ borderRadius: 20, fontSize: 11, padding: '3px 10px' }}
                                    >
                                        {label}
                                    </Button>
                                ))}
                            </div>

                            {batchLoading ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" size="sm" style={{ color: colors.blue }} />
                                </div>
                            ) : batches.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="nc-icon nc-credit-card" style={{ fontSize: '48px', color: colors.line }} />
                                    <p className="mt-3" style={{ color: colors.muted }}>
                                        No batch payouts yet. Batches are initiated by Lusuku admin on your payout schedule.
                                    </p>
                                </div>
                            ) : (
                                <Table responsive hover>
                                    <thead>
                                        <tr style={{ fontSize: 12, color: colors.muted, textTransform: 'uppercase' }}>
                                            <th>#</th>
                                            <th>Period</th>
                                            <th>Riders</th>
                                            <th>Net Payout</th>
                                            <th>MTN Fees</th>
                                            <th>Progress</th>
                                            <th>Status</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batches.map((batch, idx) => {
                                            const meta = BATCH_STATUS_META[batch.status] || BATCH_STATUS_META['PREPARING'];
                                            const progressPct = batch.totalPayees > 0
                                                ? Math.round((batch.processedCount / batch.totalPayees) * 100)
                                                : 0;
                                            const isExpanded = expandedBatch === batch.batchId;
                                            return (
                                                <React.Fragment key={batch.batchId}>
                                                    <tr
                                                        style={{ cursor: 'pointer', background: isExpanded ? colors.ground : 'transparent' }}
                                                        onClick={() => setExpandedBatch(isExpanded ? null : batch.batchId)}
                                                    >
                                                        <td style={{ color: colors.muted, fontSize: 12 }}>
                                                            {(batchPage - 1) * 15 + idx + 1}
                                                        </td>
                                                        <td style={{ fontSize: 12 }}>
                                                            {batch.periodStart ? (
                                                                <>
                                                                    <div>{formatDate(batch.periodStart)}</div>
                                                                    <small style={{ color: colors.muted }}>→ {formatDate(batch.periodEnd)}</small>
                                                                </>
                                                            ) : '—'}
                                                        </td>
                                                        <td><strong>{batch.totalPayees}</strong></td>
                                                        <td>
                                                            <strong style={{ color: batch.status === 'COMPLETED' ? colors.green : colors.body }}>
                                                                {formatCurrency(batch.totalNetAmount)}
                                                            </strong>
                                                        </td>
                                                        <td style={{ color: colors.red, fontSize: 12 }}>
                                                            {formatCurrency(batch.totalMtnFees)}
                                                        </td>
                                                        <td style={{ minWidth: 100 }}>
                                                            {batch.status === 'PROCESSING' || batch.status === 'COMPLETED' || batch.status === 'PARTIALLY_COMPLETED' ? (
                                                                <>
                                                                    <ProgressBar
                                                                        now={progressPct}
                                                                        variant={progressPct === 100 ? 'success' : 'info'}
                                                                        style={{ height: 6 }}
                                                                    />
                                                                    <small style={{ fontSize: 10, color: colors.muted }}>
                                                                        {batch.successCount}/{batch.totalPayees} paid
                                                                    </small>
                                                                </>
                                                            ) : '—'}
                                                        </td>
                                                        <td>
                                                            <Badge bg={meta.variant} style={{ fontSize: 10 }}>
                                                                {meta.label}
                                                            </Badge>
                                                        </td>
                                                        <td style={{ fontSize: 11, color: colors.muted }}>
                                                            {formatDate(batch.createdAt)}
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={8} style={{ background: colors.ground, padding: '12px 20px' }}>
                                                                <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', fontSize: 12 }}>
                                                                    <div>
                                                                        <span style={{ color: colors.muted }}>Batch ID</span><br />
                                                                        <code style={{ fontSize: 11 }}>{batch.batchId}</code>
                                                                    </div>
                                                                    <div>
                                                                        <span style={{ color: colors.muted }}>Total Debit (incl. fees)</span><br />
                                                                        <strong>{formatCurrency(batch.totalMomoDebit)}</strong>
                                                                    </div>
                                                                    {batch.failedCount > 0 && (
                                                                        <div>
                                                                            <span style={{ color: colors.red }}>Failed</span><br />
                                                                            <strong style={{ color: colors.red }}>{batch.failedCount} riders</strong>
                                                                        </div>
                                                                    )}
                                                                    {batch.approvedBy && (
                                                                        <div>
                                                                            <span style={{ color: colors.muted }}>Approved at</span><br />
                                                                            <strong>{formatDate(batch.approvedAt)}</strong>
                                                                        </div>
                                                                    )}
                                                                    {batch.completedAt && (
                                                                        <div>
                                                                            <span style={{ color: colors.muted }}>Completed at</span><br />
                                                                            <strong>{formatDate(batch.completedAt)}</strong>
                                                                        </div>
                                                                    )}
                                                                    {batch.errorSummary && (
                                                                        <div>
                                                                            <span style={{ color: colors.red }}>Error</span><br />
                                                                            <span style={{ color: colors.red }}>{batch.errorSummary}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}

                            {/* Pagination */}
                            {batchPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        disabled={batchPage <= 1}
                                        onClick={() => setBatchPage((p) => Math.max(1, p - 1))}
                                    >← Prev</Button>
                                    <small style={{ color: colors.muted }}>Page {batchPage} of {batchPages}</small>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        disabled={batchPage >= batchPages}
                                        onClick={() => setBatchPage((p) => Math.min(batchPages, p + 1))}
                                    >Next →</Button>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PartnerEarnings;
